import { Component, SecurityContext } from '@angular/core';
import { DomSanitizer, SafeValue } from '@angular/platform-browser';

/**
 * A component that displays alert messages with sanitized HTML content.
 * 
 * This component is used internally by the KendoAlertService to render alert dialog content.
 * It safely displays HTML content by properly sanitizing it to prevent XSS attacks while
 * still allowing formatted text.
 * 
 * @example
 * ```html
 * <kendo-alert></kendo-alert>
 * ```
 */
@Component({
  selector: 'kendo-alert',
  template: `<div [innerHtml]="html"></div>`,
  standalone: true,
})
export class KendoAlertComponent {
  /**
   * The sanitized HTML content to be displayed.
   */
  html?: SafeValue;
  
  /** @internal */
  private _msg: string = '';
  
  /**
   * Gets the message text before HTML sanitization.
   * @returns The current message text.
   */
  get msg(): string {
    return this._msg;
  }
  
  /**
   * Sets the message text and converts it to sanitized HTML.
   * @param value The message text to display, may contain HTML.
   */
  set msg(value: string) {
    this._msg = value;
    this.html = this.getHtml(value);
  }

  /**
   * Creates a new instance of the KendoAlertComponent.
   * @param sanitizer The Angular DomSanitizer for securing HTML content.
   */
  constructor(private sanitizer: DomSanitizer) {}
  
  /**
   * Converts string content to sanitized HTML that can be safely rendered.
   * @param html The HTML string to sanitize.
   * @returns A SafeValue that can be used with innerHtml directive.
   * @internal
   */
  getHtml(html: string) {
    let safeHtml = this.sanitizer.sanitize(SecurityContext.STYLE, html)!;
    return this.sanitizer.bypassSecurityTrustHtml(safeHtml);
  }
}
